import { Component, OnDestroy, OnInit, Signal } from '@angular/core';
import { ApplicationAppEntityApplicationRead } from '../../../../../openapi';
import {
  selectApplication,
  selectLoading,
  selectPending
} from '../../../../../store/selectors/application.selectors';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { LocalisationForm } from './forms/localisation-form';
import { updateParcels } from '../../../../../store/actions/application.actions';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ValidationErrorsComponent } from '../../../../../common/validation-errors/validation-errors.component';
import { DatePickerComponent } from '../../../../../common/date-picker/date-picker.component';
import { ParcelsCrudComponent } from '../components/parcels/parcels-crud/parcels-crud.component';

export interface LocalisationDataComponentConfig {
  stages: string[];
  successUrl: string;
  cancelUrl: string;
  addStreetControl?: boolean;
  streetControlRequired?: boolean;
  addHouseNumberControl?: boolean;
  houseNumberControlRequired?: boolean;
  addCityControl?: boolean;
  addTaskNameControl?: boolean;
  addAgreementControls?: boolean;
  addSurfaceTypeControl?: boolean;
}

@Component({
  selector: 'app-localisation-data-section',
  standalone: true,
  imports: [
    StagesComponent,
    SpinnerComponent,
    ReactiveFormsModule,
    CommonModule,
    ValidationErrorsComponent,
    DatePickerComponent,
    ParcelsCrudComponent
  ],
  providers: [LocalisationForm],
  templateUrl: './localisation-data-section.component.html'
})
export class LocalisationDataSectionComponent implements OnInit, OnDestroy {

  public successUrl: string | undefined = this.route.snapshot.data['successUrl'];

  public cancelUrl: string | undefined = this.route.snapshot.data['cancelUrl'];

  public stages: string[] = this.route.snapshot.data['stages'];

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  private fetchApplication: Subscription = toObservable(this.application).subscribe((app) => {
    if (app) {
      this.form.patchFormValues(app);
    }
  });

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store,
    public form: LocalisationForm
  ) {
  }

  public submitAndGoNext(): void {
    const currentApp = this.application();
    if (this.form.valid && currentApp) {
      this.store$.dispatch(updateParcels({
        currentApp,
        payload: this.form.toWriteModel(),
        parcels: this.form.parcelsValues,
        successFnc: (applicationId: string) => {
          this.router.navigate(
            [this.successUrl],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        },
        tag: 'parcels'
      }));
    } else {
      this.form.markAllAsTouched();
    }
  }

  public onCancel(): void {
    this.router.navigate([this.cancelUrl], { relativeTo: this.route });
  }

  public ngOnInit(): void {
    if (this.route.snapshot.data['addStreetControl']) {
      this.form.createStreetControl(this.route.snapshot.data['streetControlRequired']);
    }
    if (this.route.snapshot.data['addHouseNumberControl']) {
      this.form.createHouseNumberControl(this.route.snapshot.data['houseNumberControlRequired']);
    }
    if (this.route.snapshot.data['addCityControl']) {
      this.form.createCityControl();
    }
    if (this.route.snapshot.data['addTaskNameControl']) {
      this.form.createTaskNameControl();
    }
    if (!!this.route.snapshot.data['addAgreementControls']) {
      this.form.createAgreementControls();
    }
    if (!!this.route.snapshot.data['addSurfaceTypeControl']) {
      this.form.createSurfaceTypeControl();
    }
  }

  public ngOnDestroy(): void {
    this.fetchApplication.unsubscribe();
  }

  protected readonly Validators = Validators;
}
